import { useState, useRef, useEffect, useMemo, KeyboardEvent, ChangeEvent, ClipboardEvent, forwardRef, useImperativeHandle } from 'react';
import { Send, Smile, Paperclip, X, FileText, Image as ImageIcon, Zap } from 'lucide-react';
import { cn } from '../../lib/cn';
import { MentionAutocomplete } from './MentionAutocomplete';
import { HashLinkAutocomplete } from './HashLinkAutocomplete';
import { EmojiPicker } from './EmojiPicker';
import { GifPicker } from './GifPicker';
import { replaceEmojiShortcodes } from './emojiShortcodes';
import { pickImageFiles } from './clipboardImages';
import { useChannelDraft } from '../../hooks/useChannelDraft';
import type { CollaboratorSummary } from '../../hooks/queries/useMessaging';
import { useHashLinkSearch } from '../../hooks/queries/useHashLinkSearch';
import {
  applyHashLinkSelection,
  detectHashQuery,
  groupHashLinkHits,
  serializeHashLink,
  type HashLinkHit,
} from '../../lib/messaging/hashLink';

interface MessageComposerProps {
  onSend: (body: string, attachments?: File[]) => void;
  disabled?: boolean;
  collaborators: CollaboratorSummary[];
  placeholder?: string;
  channelId: string;
  onStartTyping?: () => void;
  onStopTyping?: () => void;
  allowAttachments?: boolean;
  /** Wizz (nudge MSN) : envoie un tremblement au correspondant. Absent = bouton masqué. */
  onWizz?: () => void;
  /** Grise le bouton Wizz pendant le cooldown / si le canal n'est pas prêt. */
  wizzDisabled?: boolean;
  /**
   * Contenu initial du champ. Utilisé en mode édition pour précharger le message
   * existant. N'a d'effet qu'avec `useDraft = false` (sinon le brouillon persisté
   * fait foi).
   */
  initialValue?: string;
  /**
   * Persiste la saisie dans le brouillon localStorage du canal (défaut : true).
   * Passer `false` en mode édition : on part de `initialValue` et on ne touche
   * jamais au brouillon d'envoi du canal (sinon éditer écraserait le message en
   * cours de rédaction, et annuler laisserait le corps édité dans le champ d'envoi).
   */
  useDraft?: boolean;
}

export interface MessageComposerHandle {
  addFiles: (files: FileList | File[]) => void;
}

type ActivePicker = 'emoji' | 'gif' | null;

const MAX_FILES = 10;
const MAX_SIZE = 10 * 1024 * 1024; // 10 Mo

const ALLOWED_MIME = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/zip',
  'application/x-zip-compressed',
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/heif',
  'image/svg+xml',
  'text/html',
];

function formatBytes(n: number): string {
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
}

export const MessageComposer = forwardRef<MessageComposerHandle, MessageComposerProps>(function MessageComposer({
  onSend,
  disabled,
  collaborators,
  placeholder,
  channelId,
  onStartTyping,
  onStopTyping,
  allowAttachments = true,
  onWizz,
  wizzDisabled = false,
  initialValue = '',
  useDraft = true,
}, ref) {
  // Deux sources de vérité mutuellement exclusives (les deux hooks sont appelés
  // inconditionnellement — règle des hooks — mais on n'en garde qu'une) :
  //  • brouillon persisté par canal (envoi normal) : survit à la fermeture du
  //    sidecar et au rechargement, ne fuit pas d'un canal à l'autre ;
  //  • état local seedé sur initialValue (édition) : isolé du brouillon d'envoi.
  const draft = useChannelDraft(channelId);
  const local = useState(initialValue);
  const [value, setValue] = useDraft ? draft : local;
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [hashQuery, setHashQuery] = useState<string | null>(null);
  const [hashActiveIndex, setHashActiveIndex] = useState(0);
  const [activePicker, setActivePicker] = useState<ActivePicker>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const gifButtonRef = useRef<HTMLButtonElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hashSearch = useHashLinkSearch(hashQuery);
  const hashSections = useMemo(
    () => (hashQuery !== null ? groupHashLinkHits(hashSearch.data ?? []) : []),
    [hashQuery, hashSearch.data],
  );
  const hashHits = useMemo(
    () => hashSections.flatMap((section) => section.hits),
    [hashSections],
  );

  useEffect(() => {
    setHashActiveIndex(0);
  }, [hashQuery, hashSearch.data]);

  useImperativeHandle(ref, () => ({ addFiles }), []);

  // Autofocus à l'ouverture du composer (changement de canal ou mount).
  // En édition, on place le curseur en fin de texte préchargé plutôt qu'au début.
  useEffect(() => {
    if (disabled) return;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    if (!useDraft) {
      const end = el.value.length;
      el.setSelectionRange(end, end);
    }
  }, [channelId, disabled, useDraft]);

  function handleChange(e: ChangeEvent<HTMLTextAreaElement>) {
    const text = e.target.value;
    setValue(text);
    if (text.trim()) onStartTyping?.(); else onStopTyping?.();

    const cursorPos = e.target.selectionStart ?? text.length;
    const textBeforeCursor = text.slice(0, cursorPos);
    const mentionMatch = textBeforeCursor.match(/@([\w\sÀ-ɏ]*)$/);
    if (mentionMatch) {
      setMentionQuery(mentionMatch[1] ?? '');
      setHashQuery(null);
    } else {
      setMentionQuery(null);
      setHashQuery(detectHashQuery(textBeforeCursor));
    }

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 150) + 'px';
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (hashQuery !== null) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (hashHits.length) {
          setHashActiveIndex((i) => (i + 1) % hashHits.length);
        }
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (hashHits.length) {
          setHashActiveIndex((i) => (i - 1 + hashHits.length) % hashHits.length);
        }
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        const hit = hashHits[hashActiveIndex];
        if (hit) handleHashSelect(hit);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setHashQuery(null);
        setActivePicker(null);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    if (e.key === 'Escape') {
      setMentionQuery(null);
      setHashQuery(null);
      setActivePicker(null);
    }
  }

  function addFiles(files: FileList | File[]) {
    setError(null);
    const arr = Array.from(files);
    const accepted: File[] = [];
    for (const f of arr) {
      if (f.size > MAX_SIZE) {
        setError(`« ${f.name} » dépasse la limite de 10 Mo.`);
        continue;
      }
      if (!f.type || !ALLOWED_MIME.includes(f.type)) {
        setError(`Type de fichier non autorisé : ${f.type || f.name}.`);
        continue;
      }
      accepted.push(f);
    }
    setPendingFiles((prev) => {
      const next = [...prev, ...accepted];
      if (next.length > MAX_FILES) {
        setError(`Maximum ${MAX_FILES} pièces jointes par message.`);
        return next.slice(0, MAX_FILES);
      }
      return next;
    });
  }

  function removeFile(index: number) {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  }

  function handleSend() {
    const trimmed = replaceEmojiShortcodes(value.trim());
    const hasText = trimmed.length > 0;
    const hasFiles = pendingFiles.length > 0;
    if (disabled || (!hasText && !hasFiles)) return;
    onSend(trimmed, hasFiles ? pendingFiles : undefined);
    setValue('');
    setPendingFiles([]);
    setError(null);
    setMentionQuery(null);
    setHashQuery(null);
    setActivePicker(null);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    onStopTyping?.();
  }

  function handleEmojiSelect(emoji: string) {
    if (!textareaRef.current) return;
    const pos = textareaRef.current.selectionStart ?? value.length;
    const next = value.slice(0, pos) + emoji + value.slice(pos);
    setValue(next);
    setActivePicker(null);
    setTimeout(() => {
      textareaRef.current?.focus();
      const newPos = pos + emoji.length;
      textareaRef.current?.setSelectionRange(newPos, newPos);
    }, 0);
  }

  function handleGifSelect(gifUrl: string) {
    onSend(gifUrl);
    setActivePicker(null);
  }

  function handleHashSelect(hit: HashLinkHit) {
    if (!textareaRef.current) return;
    const cursorPos = textareaRef.current.selectionStart ?? value.length;
    const token = serializeHashLink(hit);
    const next = applyHashLinkSelection(value, cursorPos, token);
    setValue(next.text);
    setHashQuery(null);
    setTimeout(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(next.cursor, next.cursor);
    }, 0);
  }

  function handleMentionSelect(collab: CollaboratorSummary) {
    if (!textareaRef.current) return;
    const cursorPos = textareaRef.current.selectionStart ?? value.length;
    const replaced = value.slice(0, cursorPos).replace(/@[\wÀ-ɏ\s]*$/, `@${collab.full_name} `);
    setValue(replaced + value.slice(cursorPos));
    setMentionQuery(null);
    setTimeout(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(replaced.length, replaced.length);
    }, 0);
  }

  function togglePicker(picker: ActivePicker) {
    setActivePicker((prev) => (prev === picker ? null : picker));
    setMentionQuery(null);
    setHashQuery(null);
  }

  function handleFileInputChange(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.files) addFiles(e.target.files);
    e.target.value = '';
  }

  // Coller une capture d'écran (Ctrl+V depuis l'outil système) → PJ image.
  function handlePaste(e: ClipboardEvent<HTMLTextAreaElement>) {
    if (!allowAttachments) return;
    const images = pickImageFiles(e.clipboardData);
    if (images.length > 0) {
      e.preventDefault(); // évite de coller le nom du fichier en texte
      addFiles(images);
    }
  }

  const canSend = !disabled && (value.trim().length > 0 || pendingFiles.length > 0);

  return (
    <div className="relative border-t border-cime-border bg-cime-bg">
      {/* Liste des PJ en attente */}
      {pendingFiles.length > 0 && (
        <div className="flex flex-wrap gap-2 px-3 pt-2">
          {pendingFiles.map((f, i) => {
            const isImage = f.type.startsWith('image/');
            return (
              <div
                key={`${f.name}-${i}`}
                className="flex items-center gap-2 bg-cime-surface border border-cime-border rounded-lg px-2 py-1 text-xs"
              >
                {isImage ? (
                  <ImageIcon className="w-3.5 h-3.5 text-cime-text-muted" />
                ) : (
                  <FileText className="w-3.5 h-3.5 text-cime-text-muted" />
                )}
                <span className="max-w-[180px] truncate" title={f.name}>{f.name}</span>
                <span className="text-[10px] text-cime-text-muted">{formatBytes(f.size)}</span>
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="p-0.5 rounded hover:bg-cime-bg text-cime-text-muted hover:text-red-500"
                  aria-label={`Retirer ${f.name}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {error && (
        <div className="px-3 pt-2 text-xs text-red-500">{error}</div>
      )}

      <div className="relative flex items-end gap-2 p-3">
        {activePicker === 'emoji' && (
          <EmojiPicker onSelect={handleEmojiSelect} onClose={() => setActivePicker(null)} />
        )}
        {activePicker === 'gif' && (
          <GifPicker anchorRef={gifButtonRef} onSelect={handleGifSelect} onClose={() => setActivePicker(null)} />
        )}

        {mentionQuery !== null && (
          <MentionAutocomplete
            query={mentionQuery}
            collaborators={collaborators}
            onSelect={handleMentionSelect}
            onClose={() => setMentionQuery(null)}
          />
        )}

        {hashQuery !== null && (
          <HashLinkAutocomplete
            sections={hashSections}
            activeIndex={hashActiveIndex}
            onSelect={handleHashSelect}
            onClose={() => setHashQuery(null)}
          />
        )}

        {allowAttachments && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ALLOWED_MIME.join(',')}
              onChange={handleFileInputChange}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || pendingFiles.length >= MAX_FILES}
              className={cn(
                'flex items-center justify-center w-7 h-7 rounded-md transition-colors flex-shrink-0',
                'text-cime-text-muted hover:bg-cime-surface hover:text-cime-text-primary',
                (disabled || pendingFiles.length >= MAX_FILES) && 'opacity-50 cursor-not-allowed',
              )}
              aria-label="Joindre un fichier"
              title="Joindre un fichier"
            >
              <Paperclip className="w-4 h-4" />
            </button>
          </>
        )}

        <button
          type="button"
          onClick={() => togglePicker('emoji')}
          disabled={disabled}
          className={cn(
            'flex items-center justify-center w-7 h-7 rounded-md transition-colors flex-shrink-0',
            activePicker === 'emoji'
              ? 'bg-cime-primary/10 text-cime-primary'
              : 'text-cime-text-muted hover:bg-cime-surface hover:text-cime-text-primary',
            disabled && 'opacity-50 cursor-not-allowed',
          )}
          aria-label="Emojis"
        >
          <Smile className="w-4 h-4" />
        </button>

        <button
          ref={gifButtonRef}
          type="button"
          onClick={() => togglePicker('gif')}
          disabled={disabled}
          className={cn(
            'flex items-center justify-center w-7 h-7 rounded-md transition-colors flex-shrink-0',
            activePicker === 'gif'
              ? 'bg-cime-primary/10 text-cime-primary'
              : 'text-cime-text-muted hover:bg-cime-surface hover:text-cime-text-primary',
            disabled && 'opacity-50 cursor-not-allowed',
          )}
          aria-label="GIFs"
        >
          <span className="text-[10px] font-bold tracking-tight">GIF</span>
        </button>

        {onWizz && (
          <button
            type="button"
            onClick={onWizz}
            disabled={disabled || wizzDisabled}
            className={cn(
              'flex items-center justify-center w-7 h-7 rounded-md transition-colors flex-shrink-0',
              'text-cime-text-muted hover:bg-cime-surface hover:text-cime-primary',
              (disabled || wizzDisabled) && 'opacity-50 cursor-not-allowed',
            )}
            aria-label="Wizz"
            title={wizzDisabled ? 'Patiente avant de renvoyer un Wizz' : 'Envoyer un Wizz (fait trembler la fenêtre du correspondant)'}
          >
            <Zap className="w-4 h-4" />
          </button>
        )}

        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={placeholder ?? 'Écrire un message… (Entrée pour envoyer, Maj+Entrée pour saut de ligne)'}
          rows={1}
          className={cn(
            'flex-1 resize-none bg-cime-surface border border-cime-border rounded-lg px-3 py-2',
            'text-sm text-cime-text-primary placeholder:text-cime-text-muted',
            'focus:outline-none focus:ring-2 focus:ring-cime-primary/30 focus:border-cime-primary',
            'transition-colors min-h-[40px] max-h-[150px] overflow-y-auto',
          )}
        />

        <button
          type="button"
          onClick={handleSend}
          disabled={!canSend}
          className={cn(
            'flex items-center justify-center w-8 h-8 rounded-md transition-colors flex-shrink-0',
            canSend
              ? 'bg-cime-primary text-white hover:opacity-90'
              : 'bg-cime-surface text-cime-text-muted cursor-not-allowed border border-cime-border',
          )}
          aria-label="Envoyer"
        >
          <Send className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
});
